/**
 * Date helpers. Dates on items are calendar dates (YYYY-MM-DD) with no timezone,
 * so everything here parses and formats them as local calendar days rather than
 * going through UTC instants, which is what makes "due today" mean today.
 */

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));
export const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function fromISO(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export const todayISO = () => toISO(new Date());

export function addDays(iso, days) {
  const date = fromISO(iso);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return toISO(date);
}

export function diffDays(fromIso, toIso) {
  const a = fromISO(fromIso);
  const b = fromISO(toIso);
  if (!a || !b) return null;
  return Math.round((b - a) / 86400000);
}

/** Monday-anchored start of the week containing `iso`. */
export function startOfWeek(iso) {
  const date = fromISO(iso) || new Date();
  const offset = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - offset);
  return toISO(date);
}

export function formatDate(iso, { withYear } = {}) {
  const date = fromISO(iso);
  if (!date) return '';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return withYear || !sameYear
    ? `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
    : `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}`;
}

export function formatRange(start, end) {
  if (!start || !end) return '';
  const a = fromISO(start);
  const b = fromISO(end);
  if (!a || !b) return '';
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${MONTHS_SHORT[a.getMonth()]} ${a.getDate()} - ${b.getDate()}`;
  }
  return `${formatDate(start)} - ${formatDate(end)}`;
}

/** "Overdue by 3 days" / "Today" / "in 5 days" for due-date affordances. */
export function dueLabel(iso) {
  const delta = diffDays(todayISO(), iso);
  if (delta == null) return { text: '', tone: 'none' };
  if (delta < -1) return { text: `${Math.abs(delta)} days overdue`, tone: 'overdue' };
  if (delta === -1) return { text: 'Yesterday', tone: 'overdue' };
  if (delta === 0) return { text: 'Today', tone: 'today' };
  if (delta === 1) return { text: 'Tomorrow', tone: 'soon' };
  if (delta <= 7) return { text: `In ${delta} days`, tone: 'soon' };
  return { text: formatDate(iso), tone: 'none' };
}

/** "2:45 PM" - every clock time in the app reads AM/PM, not 24h. */
export function clockTime(isoTimestamp) {
  return new Date(isoTimestamp).toLocaleTimeString([], {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

/** "22 Aug, 2:45 PM" (year appended only when it is not this year). */
export function dateTimeLabel(isoTimestamp) {
  const when = new Date(isoTimestamp);
  const sameYear = when.getFullYear() === new Date().getFullYear();
  const day = when.toLocaleDateString([], {
    day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${day}, ${clockTime(isoTimestamp)}`;
}

export function relativeTime(isoTimestamp) {
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.round(seconds / 86400)}d ago`;
  // Older than a week, the date alone answered "when" for lists - but for
  // messages "Aug 10" without a time reads as half an answer. Both surfaces
  // want the same thing once you ask what a timestamp is FOR.
  const at = new Date(then);
  return `${formatDate(toISO(at))}, ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/** The 6x7 day grid for a month view, including the leading/trailing days. */
export function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return {
      iso: toISO(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month,
      isToday: toISO(date) === todayISO(),
      isWeekend: [0, 6].includes(date.getDay()),
    };
  });
}

/** Inclusive list of ISO dates from start to end. */
export function eachDay(startIso, endIso) {
  const days = [];
  let cursor = startIso;
  let guard = 0;
  while (cursor && cursor <= endIso && guard < 2000) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
    guard += 1;
  }
  return days;
}
